import React from 'react';
import { useNavigate } from 'react-router-dom';
import { infoPageStyles as styles } from './sharedStyles';
import { COLORS, LAYOUT } from '../../constants';

const HowItWorks = () => {
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      title: 'Create Your Account',
      description: 'Sign up using your ISU email address. Verify your email to access all features of Redbird Markets.'
    },
    {
      number: 2,
      title: 'Browse or List Items',
      description: 'Search the marketplace for items you need, or create listings to sell items you no longer use.'
    },
    {
      number: 3,
      title: 'Connect with Sellers/Buyers',
      description: 'Use our secure messaging system to discuss details, ask questions, and arrange meetings.'
    },
    {
      number: 4,
      title: 'Complete the Transaction',
      description: 'Meet in a safe, public location on campus. Complete the purchase securely through our platform.'
    }
  ];

  return (
    <div style={styles.pageContainer}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>How It Works</h1>
        <p style={styles.headerSubtitle}>
          Your guide to buying and selling on Redbird Markets
        </p>
      </div>

      <div style={styles.contentContainer}>
        {/* Overview Section */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Welcome to Redbird Markets</h2>
          <p style={styles.text}>
            Redbird Markets is the official marketplace for the Illinois State University community. 
            Our platform makes it easy and safe for students, faculty, and staff to buy and sell 
            items within the ISU community.
          </p>
          <p style={styles.text}>
            Whether you're looking for textbooks, furniture, electronics, or anything else, 
            Redbird Markets connects you with trusted members of the ISU community.
          </p>
        </div>

        {/* Steps Section */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Getting Started in 4 Easy Steps</h2>
          
          {steps.map((step, index) => (
            <div key={step.number}>
              <div style={{
                display: 'flex',
                gap: LAYOUT.spacing.lg,
                marginBottom: LAYOUT.spacing.xl,
                alignItems: 'flex-start'
              }}>
                <div style={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${COLORS.primary}, #9d0d1d)`,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 28,
                  fontWeight: 700,
                  flexShrink: 0,
                  boxShadow: `0 4px 12px ${COLORS.primary}40`
                }}>
                  {step.number}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={styles.subsectionTitle}>{step.title}</h3>
                  <p style={styles.text}>{step.description}</p>
                </div>
              </div>
              {index < steps.length - 1 && <hr style={styles.divider} />}
            </div>
          ))}
        </div>

        {/* For Buyers Section */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>For Buyers</h2>
          
          <h3 style={styles.subsectionTitle}>Finding Items</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Browse the marketplace by category or use the search function</li>
            <li style={styles.listItem}>Filter results by prices</li>
            <li style={styles.listItem}>View detailed photos and descriptions of items</li>
          </ul>

          <h3 style={styles.subsectionTitle}>Making a Purchase</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Contact the seller through our secure messaging system</li>
            <li style={styles.listItem}>Ask questions about the item's condition, availability, and pickup details</li>
            <li style={styles.listItem}>Schedule a meeting time and location (always choose public spaces)</li>
            <li style={styles.listItem}>Complete payment through our secure platform</li>
            <li style={styles.listItem}>Inspect the item before finalizing the transaction</li>
          </ul>
        </div>

        {/* For Sellers Section */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>For Sellers</h2>
          
          <h3 style={styles.subsectionTitle}>Creating a Listing</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Click "Sell" in the navigation menu</li>
            <li style={styles.listItem}>Upload clear, well-lit photos of your item</li>
            <li style={styles.listItem}>Write a detailed, honest description including condition and any flaws</li>
            <li style={styles.listItem}>Set a fair price based on the item's condition and market value</li>
            <li style={styles.listItem}>Choose appropriate categories and tags for better visibility</li>
          </ul>

          <h3 style={styles.subsectionTitle}>Managing Sales</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Respond promptly to buyer inquiries</li>
            <li style={styles.listItem}>Be honest about item condition and any issues</li>
            <li style={styles.listItem}>Arrange safe meeting locations and times</li>
            <li style={styles.listItem}>Update or delete listings as needed from "My Items"</li>
          </ul>

          <h3 style={styles.subsectionTitle}>After Your Item Sells</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Your listing will automatically be marked as "sold"</li>
            <li style={styles.listItem}>Use "Contact Buyer" to communicate post-sale</li>
            <li style={styles.listItem}>Archive completed sales to keep your dashboard organized</li>
            <li style={styles.listItem}>Your sales history and transaction records are always preserved</li>
          </ul>
        </div>

        {/* Payment & Security */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Payment & Security</h2>
          
          <div style={styles.highlight}>
            <h3 style={styles.highlightTitle}>Secure Payments</h3>
            <p style={styles.text}>
              Redbird Markets uses Stripe for secure payment processing. Your financial information 
              is encrypted and never shared with sellers.
            </p>
          </div>

          <h3 style={styles.subsectionTitle}>How Seller Payments Work</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Automatic Deposits:</strong> When something sells, Stripe deposits money directly to your bank account within 1-2 business days</li>
            <li style={styles.listItem}><strong>No Manual Withdrawals:</strong> You don't need to "withdraw" anything - payments are completely automatic</li>
            <li style={styles.listItem}><strong>Transparent Fees:</strong> 5% platform fee + Stripe processing (2.9% + $0.30) are deducted before deposit</li>
            <li style={styles.listItem}><strong>Daily Payouts:</strong> Stripe processes payouts daily, so you get paid fast</li>
          </ul>

          <h3 style={styles.subsectionTitle}>Safety Tips</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Always meet in public, well-lit areas on campus</li>
            <li style={styles.listItem}>Bring a friend when possible</li>
            <li style={styles.listItem}>Inspect items before completing payment</li>
            <li style={styles.listItem}>Trust your instincts - if something feels wrong, walk away</li>
            <li style={styles.listItem}>Report suspicious activity or users immediately</li>
          </ul>
        </div>

        {/* Get Started CTA */}
        <div style={{
          ...styles.section,
          textAlign: 'center'
        }}>
          <h2 style={styles.sectionTitle}>Ready to Get Started?</h2>
          <p style={styles.text}>
            Join the Redbird Markets community today and start buying and selling with fellow ISU members.
          </p>
          <button
            style={styles.button}
            onClick={() => navigate('/market')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 6px 20px ${COLORS.primary}40`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 4px 12px ${COLORS.primary}30`;
            }}
          >
            Explore the Marketplace
          </button>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
